import { z } from 'zod';
import { agentUpdateStateSchema } from './agent-auto-update';
import { ENDPOINT_FILTER_FIELD } from './endpoints';
import { paginationSchema, sortOrderSchema } from './helpers';
import { ColumnDataTypes, FilterOperators, TableResponseMetaSchema, createTableQuerySchema, } from './table';
/*
  KIR-5411 (KIR-5336) — Inventory → Endpoints: one row per physical machine.

  A device is the org's `endpoints` rows (one per person per machine) grouped
  by the hashed hardware ID, plus the `devices` row when the machine is
  enrolled (KIR-5407). An endpoint that has not sent a hardware ID yet is its
  own device. Gated by the `shared_devices` flag; Owner, Manager or Support.
*/
/** Days a person must have been seen on a device to count under "People seen". */
export const DEVICE_PEOPLE_SEEN_DAYS = 30;
/** Days of alerts counted per device (same window as the Users tab). */
export const DEVICE_ALERT_WINDOW_DAYS = 7;
/** Most people named on a list row; the full list is on the device page. */
export const DEVICE_PEOPLE_PREVIEW_LIMIT = 3;
/** Most AI tools named on a list row; the full list is on the device page. */
export const DEVICE_AI_TOOLS_PREVIEW_LIMIT = 3;
/** Most alert rows on the device page (most severe, then most recent, first). */
export const DEVICE_ALERT_ROWS_LIMIT = 50;
/**
 * Opaque device id. `hw-<hardware ID>` for a machine that sends one,
 * `ep-<endpoints.id>` for an endpoint that does not yet.
 */
export const deviceIdSchema = z
    .string()
    .regex(/^(hw-[a-f0-9]{64}|ep-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/, 'Expected a device id');
/** OS family derived from the reported OS string, for the OS filter. */
export const deviceOsFamilySchema = z.enum([
    'windows',
    'macos',
    'linux',
    'unknown',
]);
/**
 * Who a person seen on a device is. `member`: a full Kirin user. `discovered`:
 * known by email only, no seat (Cowork / no-auto-provision). `unattributed`:
 * no identity. `offboarded`: removed from the org (standing offboard or
 * deactivated) and still seen on the machine.
 */
export const devicePersonIdentitySchema = z.enum([
    'member',
    'discovered',
    'unattributed',
    'offboarded',
]);
/** Alert severities, highest first. */
export const deviceAlertSeveritySchema = z.enum([
    'critical',
    'high',
    'medium',
    'low',
]);
/** A person named on a list row (preview). */
export const devicePersonRefSchema = z.object({
    userId: z.string().uuid(),
    name: z.string().nullable(),
    email: z.string().nullable(),
});
/** One row of the Endpoints tab. */
export const deviceListItemSchema = z.object({
    id: deviceIdSchema,
    /** Hashed hardware ID; null for an endpoint that has not sent one. */
    hardwareDeviceId: z.string().nullable(),
    /** The per-person endpoint IDs (`machine_id`) on this machine. */
    machineIds: z.array(z.string()),
    /** Enrolled with the org's MDM key (KIR-5407). */
    enrolled: z.boolean(),
    name: z.string().nullable(),
    osString: z.string().nullable(),
    osFamily: deviceOsFamilySchema,
    /** The machine's one agent version: from the most recently seen agent. */
    agentVersion: z.string().nullable(),
    /** Auto-update state (KIR-5191) read like the Users tab; null when unknown or the feature is off. */
    updateState: agentUpdateStateSchema.nullable(),
    /** The agent version is older than the latest release. */
    updateAvailable: z.boolean(),
    /** More than one person seen in the last 30 days. */
    shared: z.boolean(),
    peopleSeenCount: z.number().int().min(0),
    /** Most recently seen people first, up to DEVICE_PEOPLE_PREVIEW_LIMIT. */
    peopleSeen: z.array(devicePersonRefSchema),
    /** Distinct AI tools across the machine's people; null while Shadow AI is off. */
    aiToolCount: z.number().int().min(0).nullable(),
    /** Up to DEVICE_AI_TOOLS_PREVIEW_LIMIT tool names, A–Z; null while Shadow AI is off. */
    aiToolNames: z.array(z.string()).nullable(),
    mcpServerCount: z.number().int().min(0),
    /** Distinct alerts raised on this machine in the last 7 days. */
    alertCount: z.number().int().min(0),
    maxAlertSeverity: deviceAlertSeveritySchema.nullable(),
    lastSeenAt: z.string().datetime().nullable(),
});
// ---------------------------------------------------------------------------
// List query (DataTable) — same bracket filters as the other Inventory tables.
// ---------------------------------------------------------------------------
export const DEVICE_FILTERABLE_FIELDS = {
    /** KIR-5098: name or device / endpoint ID. */
    ENDPOINT: ENDPOINT_FILTER_FIELD,
    OS: 'os',
    AGENT_VERSION: 'agentVersion',
    UPDATE_STATE: 'updateState',
    SHARED: 'shared',
};
export const DEVICE_COLUMN_DATA_TYPES = {
    endpoint: ColumnDataTypes.STRING,
    os: ColumnDataTypes.ENUM,
    agentVersion: ColumnDataTypes.ENUM,
    updateState: ColumnDataTypes.ENUM,
    shared: ColumnDataTypes.BOOLEAN,
};
export const DEVICE_OPERATOR_OVERRIDES = {
    [DEVICE_FILTERABLE_FIELDS.ENDPOINT]: [
        FilterOperators.CONTAINS,
        FilterOperators.INCLUDES,
        FilterOperators.EXCLUDES,
    ],
    [DEVICE_FILTERABLE_FIELDS.OS]: [
        FilterOperators.INCLUDES,
        FilterOperators.EXCLUDES,
    ],
};
export const deviceSortFieldSchema = z.enum([
    'name',
    'os',
    'agentVersion',
    'updateState',
    'peopleSeenCount',
    'aiToolCount',
    'mcpServerCount',
    'alertCount',
    'lastSeenAt',
]);
/** Fields the free-text search reads. */
export const DEVICE_SEARCHABLE_FIELDS = [
    'name',
    'endpoint',
    'os',
    'agentVersion',
    'people',
];
const deviceListQueryBaseSchema = paginationSchema.extend({
    sortBy: deviceSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    search: z.string().optional(),
});
export const deviceListQuerySchema = createTableQuerySchema(deviceListQueryBaseSchema, DEVICE_COLUMN_DATA_TYPES, DEVICE_OPERATOR_OVERRIDES);
export const deviceListResponseSchema = z.object({
    data: z.array(deviceListItemSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
    meta: TableResponseMetaSchema,
    /** Knostic's latest agent version; null when unknown. */
    latestAgentVersion: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Posture tiles
// ---------------------------------------------------------------------------
/** Devices per update state; `unknown` = no state reported (or feature off). */
export const deviceUpdateStateCountsSchema = z.object({
    up_to_date: z.number().int().min(0),
    updating: z.number().int().min(0),
    pending_reboot: z.number().int().min(0),
    needs_attention: z.number().int().min(0),
    update_failed: z.number().int().min(0),
    pinned_by_mdm: z.number().int().min(0),
    not_available: z.number().int().min(0),
    off: z.number().int().min(0),
    unknown: z.number().int().min(0),
});
export const devicePostureSchema = z.object({
    totalDevices: z.number().int().min(0),
    sharedDevices: z.number().int().min(0),
    singleUserDevices: z.number().int().min(0),
    /** Agent version at or past the latest release. */
    upToDateDevices: z.number().int().min(0),
    /** Agent version older than the latest release. */
    updateAvailableDevices: z.number().int().min(0),
    updateStateCounts: deviceUpdateStateCountsSchema,
    /** Devices with at least one alert in the last 7 days. */
    devicesWithAlerts: z.number().int().min(0),
    /** Distinct alerts raised on any device in the last 7 days. */
    openAlertCount: z.number().int().min(0),
    latestAgentVersion: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Filter options (endpoint name, OS family and agent version dropdowns)
// ---------------------------------------------------------------------------
export const deviceFilterOptionFieldSchema = z.enum([
    'name',
    'os',
    'agentVersion',
]);
export const deviceFilterOptionsQuerySchema = z.object({
    field: deviceFilterOptionFieldSchema,
    search: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(50).default(10),
    offset: z.coerce.number().int().min(0).default(0),
});
export const deviceFilterOptionsResponseSchema = z.object({
    options: z.array(z.string()),
    total: z.number().int().min(0),
    hasMore: z.boolean(),
});
// ---------------------------------------------------------------------------
// Device page
// ---------------------------------------------------------------------------
export const deviceAlertsBySeveritySchema = z.object({
    critical: z.number().int().min(0),
    high: z.number().int().min(0),
    medium: z.number().int().min(0),
    low: z.number().int().min(0),
});
/** One person seen on the device in the last 30 days, with their alerts there. */
export const devicePersonSchema = devicePersonRefSchema.extend({
    identity: devicePersonIdentitySchema,
    /** OS account name on this machine (e.g. CORP\alice), when the helper reported one. */
    osAccount: z.string().nullable(),
    /**
     * KIR-5670: the machine's shared Guest / kiosk sessions (one Unattributed
     * person per machine). Never offered "Link to user".
     */
    guest: z.boolean(),
    /** This person's endpoint ID on the machine. */
    machineId: z.string(),
    firstSeenAt: z.string().datetime().nullable(),
    lastSeenAt: z.string().datetime().nullable(),
    alertCount: z.number().int().min(0),
    maxAlertSeverity: deviceAlertSeveritySchema.nullable(),
    alertsBySeverity: deviceAlertsBySeveritySchema,
});
/** One AI tool on the machine and the people it was seen with. */
export const deviceAiToolSchema = z.object({
    toolId: z.string(),
    name: z.string(),
    category: z.string(),
    sources: z.array(z.string()),
    lastSeenAt: z.string().datetime().nullable(),
    seenWith: z.array(devicePersonRefSchema),
});
/**
 * One alert raised on the device in the last 7 days, by one person: the
 * device page's "Alerts on this endpoint" rows.
 */
export const deviceAlertSchema = z.object({
    findingKey: z.string(),
    type: z.string(),
    subPolicyKey: z.string().nullable(),
    /** Display title, resolved like the Alerts table's Title cell. */
    title: z.string(),
    /** Null for a severity Kirin does not recognise. */
    severity: deviceAlertSeveritySchema.nullable(),
    person: devicePersonRefSchema,
    /** The person's endpoint ID on this machine. */
    machineId: z.string(),
    lastSeenAt: z.string().datetime().nullable(),
});
export const deviceDetailSchema = deviceListItemSchema
    .omit({ peopleSeen: true })
    .extend({
    enrolledAt: z.string().datetime().nullable(),
    /** KIR-5658: when an admin revoked the enrolled machine; null when not revoked. */
    revokedAt: z.string().datetime().nullable(),
    firstSeenAt: z.string().datetime().nullable(),
    /** From the most recently seen agent on the machine. */
    ipAddresses: z.array(z.string()),
    macAddresses: z.array(z.string()),
    latestAgentVersion: z.string().nullable(),
    autoUpdateEnabled: z.boolean(),
    /** People seen in the last 30 days, most recent first, alerts split per person. */
    people: z.array(devicePersonSchema),
    /** Machine-wide AI tools; empty while Shadow AI is off (`aiToolCount` null). */
    aiTools: z.array(deviceAiToolSchema),
    /** Alerts of the last 7 days, one row per alert and person, at most DEVICE_ALERT_ROWS_LIMIT. */
    alerts: z.array(deviceAlertSchema),
});
// ---------------------------------------------------------------------------
// Link an Unattributed person to a member (KIR-5413)
// ---------------------------------------------------------------------------
/** "Link to user": the member (internal or Descope id) who gets the activity. */
export const linkDevicePersonRequestSchema = z.object({
    targetUserId: z.string().trim().min(1).max(255),
});
export const linkDevicePersonResponseSchema = z.object({
    /** The Unattributed person folded in; it no longer exists. */
    sourceUserId: z.string().uuid(),
    /** Internal id of the member that now holds the activity. */
    userId: z.string().uuid(),
});
// ---------------------------------------------------------------------------
// Revoke / reset an enrolled device (KIR-5658)
// ---------------------------------------------------------------------------
/** The device's revoke state after "Revoke device" or "Reset device". */
export const deviceRevocationResponseSchema = z.object({
    id: deviceIdSchema,
    /** When it was revoked; null after a reset (it can enroll again). */
    revokedAt: z.string().datetime().nullable(),
});
//# sourceMappingURL=device-inventory.js.map